/**
 * Shared headline % and count copy for list, Coverage tab Total cards, and modals.
 * Prefer nested analyticCoverage / collectionCoverage when present; root fields are fallback.
 * Headline surfaces must never fall back to EEI segment averages (that caused Data 60% vs 19%).
 */

import {
    buildCollectionCoverageView,
    buildCollectionEeiSegments,
    collectionCountsText as collectionCountsTextFromSegments,
    resolveIcrCollectionHeadlinePct,
} from './buildCollectionCoverageView';
import {
    analyticCountsText as analyticCountsTextFromView,
    buildAnalyticCoverageView,
} from './analytic/buildAnalyticCoverageView';

function asPct(value) {
    if (value == null || value === '') {
        return null;
    }
    const n = Number(value);
    if (!Number.isFinite(n)) {
        return null;
    }
    return Math.max(0, Math.min(100, Math.round(n)));
}

/**
 * Data (CCC) headline % for Total cards / list parity.
 * Prefers collectionCoverage.headlinePct; never averages EEI segments.
 */
export function resolveDataHeadlinePct(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') {
        return null;
    }
    const nested = snapshot.collectionCoverage;
    const fromNest = asPct(nested?.headlinePct ?? nested?.overallScorePct);
    if (fromNest != null) {
        return fromNest;
    }
    const view = buildCollectionCoverageView(snapshot);
    if (view) {
        const fromView = resolveIcrCollectionHeadlinePct(view);
        if (fromView != null) {
            return fromView;
        }
    }
    // List / overview flat fields (not root analytic headlinePct).
    return asPct(
        snapshot.dataCoveragePct
        ?? snapshot.collectionOverallScorePct
        ?? snapshot.collectionLinkedScorePct,
    );
}

/**
 * Analytic (ACC) headline % — prefers analyticCoverage nest, then root headline/overall.
 */
export function resolveAnalyticHeadlinePct(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') {
        return null;
    }
    const nest = snapshot.analyticCoverage;
    return asPct(
        nest?.headlinePct
        ?? nest?.overallPct
        ?? snapshot.headlinePct
        ?? snapshot.overallPct
        ?? snapshot.overallScore
        ?? snapshot.analyticOverallScorePct
        ?? nest?.overallScore
        ?? snapshot.analyticCoveragePct,
    );
}

function isCollectionView(value) {
    return Boolean(
        value
        && typeof value === 'object'
        && !value.collectionCoverage
        && (value.linked != null
            || value.unlinked != null
            || value.fintelUsage != null
            || Array.isArray(value.requirements)
            || Array.isArray(value.eeis)),
    );
}

/** Same EEI fully/partially covered copy as CollectionCoverageOverviewTab. */
export function dataCountsText(snapshotOrView, pirEeis = null) {
    if (!snapshotOrView || typeof snapshotOrView !== 'object') {
        return '';
    }
    const view = isCollectionView(snapshotOrView)
        ? snapshotOrView
        : buildCollectionCoverageView(snapshotOrView);
    if (!view) {
        return '';
    }
    return collectionCountsTextFromSegments(buildCollectionEeiSegments(view, pirEeis));
}

/** Same questions fully/partially answered copy as AnalyticOverviewTab. */
export function analyticCountsText(snapshotOrView) {
    if (!snapshotOrView || typeof snapshotOrView !== 'object') {
        return '';
    }
    // Accept either a built analytic view or a raw GET /coverage snapshot.
    if (snapshotOrView.answeredCount != null || snapshotOrView.totalCount != null) {
        return analyticCountsTextFromView(snapshotOrView);
    }
    const view = buildAnalyticCoverageView(snapshotOrView);
    return analyticCountsTextFromView(view);
}

export {collectionCountsTextFromSegments as collectionCountsText};
export {analyticCountsTextFromView};
